import * as React from 'react'

import { Box, Button, Table, Text, useToggle } from '@factorialco/deprecated-design-system'
import {
  TimesheetEditTimesheetRequestFragment,
  TimesheetEstimatedTimeFragment,
  TimesheetPageEmployeesSettingFragment,
  TimesheetPageShiftFragment,
  UpdateAttendanceShiftMutationVariables
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useForm } from 'modules/core/hooks/useForm'
import { useMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'
import app from 'modules/core/store/models/app'

import PendingRequestTag from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/ShiftPanelTableBody/PendingRequestTag'
import ShiftPopover from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/ShiftPopover'
import { useShiftVersionHistories } from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/shiftVersionHistoriesContext'
import Store, { EVENT_SHIFT_ACTION } from 'modules/attendance/components/ClockIn/Store'
import TimeInconsistenciesAlert from 'modules/attendance/components/TimeTracking/Modals/RestTimeAlertsAndReminders/timeInconsistenciesAlert'
import { AttendanceShiftLocationNodes } from 'modules/attendance/hooks/shift_locations/useFetchShiftLocations'
import { useShiftActions } from 'modules/attendance/hooks/shifts/useShiftActions'
import { useFetchBreakDurationRules } from 'modules/attendance/hooks/useFetchBreakDurationRules'
import { AttendanceBreakConfigurations } from 'modules/attendance/hooks/useGetAttendanceBreakConfigurationsRange'
import {
  Inconsistencies,
  shiftWithInconsistency
} from 'modules/attendance/hooks/useShiftInconsistencies'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useAttendancePolicies } from 'modules/attendance/lib/attendancePolicies'
import {
  showShiftInputBorderStyles,
  styleShiftInputBorder
} from 'modules/attendance/lib/dayRowStyle'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'
import { useTimeTrackingFeatureAvailabilityForCurrentCompany } from 'modules/attendance/lib/useTimeTrackingFeatureAvailability'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import BreakSettingsUtils from 'modules/attendance/lib/utils/BreakSettings'
import EditTimesheetRequestUtils from 'modules/attendance/lib/utils/EditTimesheetRequest'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'
import validateBreakDurationRules, {
  ErrorTypes,
  ValidationError,
  calculateOpenShiftDuration,
  isBreakSetting
} from 'modules/attendance/lib/validateShiftBreakDurationRules'

import { ClockIn, ClockOut } from './ClockInputs'
import DeleteButton from './DeleteButton'
import EditAutoBreakModal from './EditAutoBreakModal'
import CreateRequestModal from './EditTimesheetModal/CreateRequestModal'
import EditRequestModal from './EditTimesheetModal/EditRequestModal'
import PlusOneDay from './PlusOneDay'
import ShiftDetailColumns, { ObservationsMemo } from './ShiftDetailColumns'
import VersionHistory from './VersionHistory'
import { hasDisabledSubmit } from './utils'

interface Props {
  shift: TimesheetPageShiftFragment
  store: Store
  readonly?: boolean
  projectName?: string
  hasProjects?: boolean
  subprojectName: string
  isLastOpenOfDay?: boolean
  overtimeRequestMinutes: number
  dayBreaks: AttendanceBreakConfigurations[]
  restTimeInconsistencies?: ValidationError[]
  shiftLocations?: AttendanceShiftLocationNodes
  attendanceSetting?: TimesheetPageEmployeesSettingFragment
  background: 'champagne100' | 'grey100' | 'grey000White'
  selectedBreak?: AttendanceBreakConfigurations | undefined
  estimatedTime: TimesheetEstimatedTimeFragment
  trackedMinutes: number
  editTimesheetRequests: TimesheetEditTimesheetRequestFragment[]
  shifts: TimesheetPageShiftFragment[]
  inconsistencies?: Inconsistencies
}

const Separator = () => (
  <Box padding="s6">
    <Text.Normal color="grey600">–</Text.Normal>
  </Box>
)

const ShiftRow = ({
  shift,
  readonly,
  store,
  dayBreaks,
  background,
  hasProjects,
  projectName,
  selectedBreak,
  shiftLocations,
  subprojectName,
  attendanceSetting,
  overtimeRequestMinutes,
  restTimeInconsistencies,
  estimatedTime,
  trackedMinutes,
  editTimesheetRequests,
  shifts,
  inconsistencies
}: Props) => {
  const { employee } = useCurrentSession()
  const { data: policies } = useAttendancePolicies()
  const {
    clockIn,
    clockOut,
    id: shiftId,
    employeeId,
    date,
    workable,
    observations,
    timeSettingsBreakConfiguration,
    locationType
  } = shift

  const { id: timeSettingsBreakConfigurationId } = timeSettingsBreakConfiguration ?? {}
  const clockDay = AttendanceShiftUtils.getDay(shift)
  const isOpen = AttendanceShiftUtils.isOpenShift(shift)
  const isEmpty = AttendanceShiftUtils.isEmpty(shift)
  const clockInTimeStr = AttendanceShiftUtils.clockInTimeStr(shift)
  const clockOutTimeStr = AttendanceShiftUtils.clockOutTimeStr(shift)
  const isAutomaticBreak = AttendanceShiftUtils.isAutomaticBreak(shift)

  const defaultValues: UpdateAttendanceShiftMutationVariables = {
    id: shift.id,
    clockIn: clockInTimeStr,
    clockOut: clockOutTimeStr,
    locationType,
    date: shift.date,
    fetchDependencies: true
  }
  const { dateFrom, dateTo } = useAttendanceDateNavigator()
  const { updateShift } = useShiftActions()

  const { openShift, featureByName } = useTimesheetData()
  const hasPaidBreaksFeature = TimesheetFeatures.PAID_BREAKS in featureByName
  const hasAlerts = TimesheetFeatures.ALERTS in featureByName

  const form = useForm({
    defaultValues,
    onSubmit: async (data, { setErrors }) => {
      const clockInData = data.clockIn ? moment.utc(`${date} ${data.clockIn}`).toISOString() : null
      const clockOutData = data.clockOut
        ? moment.utc(`${date} ${data.clockOut}`).toISOString()
        : null

      const response = await updateShift({
        id: data.id,
        clockIn: clockInData,
        clockOut: clockOutData,
        locationType: data.locationType,
        date: shift.referenceDate,
        timeSettingsBreakConfigurationId: data.timeSettingsBreakConfigurationId,
        fetchDependencies: !(TimesheetFeatures.MATERIALIZED_DATA in featureByName)
      })

      const handled = handleResult(response, (errors) => {
        setErrors(errors)

        app.setFlash({
          type: 'wrong',
          message: errors?.base || errors?.messages || i18n.t('errors.generic_error')
        })
      })

      if (handled.success && handled.data.shift) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully')
        })

        const isBreak = !workable
        const breakType = dayBreaks.find(
          (b) => b.timeSettingsBreakConfigurationId === timeSettingsBreakConfigurationId
        )?.paid
          ? 'paid'
          : 'unpaid'

        const trackParams =
          hasPaidBreaksFeature && isBreak
            ? {
                break_type: breakType,
                break_id: timeSettingsBreakConfigurationId
              }
            : {}

        if (!(TimesheetFeatures.MATERIALIZED_DATA in featureByName)) {
          // NOTE: When materialize view is enabled, we save some resources, we skip the dispatch event
          // because the worked time materialized is not ready yet. We trigger the event in useSubscriptionWorkedTimeRefreshed hook

          dispatch({
            type: EventTypes.ATTENDANCE_SHIFT_SAVED
          })
        }

        form.resetForm()

        track(trackingEvents.timeTracking.editShiftManuallySubmit, {
          date,
          action: EVENT_SHIFT_ACTION.ADD,
          location_type: data.locationType,
          wfh_enabled: hasLocationEnabled,
          type: isBreak ? 'break' : 'shift',
          ...trackParams
        })
      }
      return handled
    }
  })

  const [showEditAutomaticBreakModal, setShowEditAutomaticBreakModal] = React.useState(false)
  const [showEditTimesheetModal, setShowEditTimesheetModal] = React.useState(false)
  const [showInconsistencyAlert, setShowInconsistencyAlert] = React.useState(false)
  const attendanceSettingIds = useMemo(() => {
    return attendanceSetting?.id ? [attendanceSetting.id] : []
  }, [attendanceSetting?.id])

  const editTimesheetAuthorizationEnabled = useTimeTrackingFeatureAvailabilityForCurrentCompany(
    'time_tracking_edit_timesheet_authorization'
  )
  // Attendance settings
  const hasBreaksEnabled = !!attendanceSetting?.enableBreaks
  const hasLocationEnabled = !!attendanceSetting?.enableClockInLocation
  const hasEditTimesheetAuthorization =
    editTimesheetAuthorizationEnabled && !!attendanceSetting?.editTimesheetAuthorization

  const hasMissingClockOut = !!EmployeesSettingsUtils.missingClockOutEnabled(attendanceSetting)
  const forgotClockOutToleranceMinutes = attendanceSetting?.forgotClockOutToleranceMinutes || 0

  const canDelete = !!policies?.employee?._policies?.deleteAttendance?.can
  const isCurrentAndOpen = openShift?.id === shift.id

  const [shiftIsUnresolved, setShiftIsUnresolved] = React.useState(isOpen)

  const isReadOnly = readonly || hasEditTimesheetAuthorization
  const showDeleteButton = !isEmpty && !readonly && canDelete && !isCurrentAndOpen
  const showPlusOneDay = shift.clockOut && shift.showPlusOneDay

  const pendingEditRequest = editTimesheetRequests.find(
    (request) => request.attendanceShift?.id === shiftId
  )
  const canOpenEditRequest = pendingEditRequest ? employee?.id === employeeId : true
  const disabledDeleteBtn =
    hasEditTimesheetAuthorization &&
    pendingEditRequest &&
    !!EditTimesheetRequestUtils.isDeleteShift(pendingEditRequest)

  const showEditModalButton = isCurrentAndOpen && !shiftIsUnresolved ? false : true

  const checkShiftIsUnresolved = async () => {
    const exceedsClockOutThreshold = store.exceedsWithForgotClockOutDuration({
      duration: AttendanceShiftUtils.hourlyDuration(shift),
      clockOutToleranceMinutes: forgotClockOutToleranceMinutes,
      overtimeRequestMinutes,
      estimatedMinutes: estimatedTime?.minutes,
      trackedMinutes
    })

    setShiftIsUnresolved(!!exceedsClockOutThreshold && isOpen)
  }

  useEffect(() => {
    form.resetForm({
      values: {
        id: shiftId,
        clockIn: clockInTimeStr,
        clockOut: clockOutTimeStr,
        date: shift.date,
        fetchDependencies: true
      }
    })

    if (isCurrentAndOpen) {
      checkShiftIsUnresolved()
    }
  }, [clockIn, clockOut, clockDay]) // eslint-disable-line react-hooks/exhaustive-deps

  const onModalSave = () => {
    checkSubmit()
    setShowInconsistencyAlert(false)
  }

  const remainingShifts = shifts.filter((currentShift) => currentShift !== shift)

  const { breakSettings, loading: loadingBreakSettings } =
    useFetchBreakDurationRules(attendanceSettingIds)
  let errors: ValidationError[] = []

  let shiftLengthInMinutes = 0
  if (form.values.clockIn && form.values.clockOut) {
    shiftLengthInMinutes = calculateOpenShiftDuration(form.values.clockIn, form.values.clockOut)
  }

  if (
    breakSettings.length > 0 &&
    isBreakSetting(breakSettings[0]) &&
    !BreakSettingsUtils.hasAutofixMissingBreaks(breakSettings[0])
  ) {
    const shiftFromForm = {
      ...shift,
      clockIn: form.values.clockIn,
      clockOut: form.values.clockOut
    }
    errors = validateBreakDurationRules(remainingShifts, breakSettings[0], shiftFromForm)
  }

  const breakLengthInconsistency = restTimeInconsistencies?.filter((error) => {
    return error.shiftId === shift.id && error.error === ErrorTypes.BREAK_MINIMUM
  })

  const checkForInconsistencies = () => {
    const errorMessage = errors[errors.length - 1]
    const { minimalDuration = 0 } = errorMessage ?? {}
    const remainingMinutes = shiftLengthInMinutes - minimalDuration

    if (!(!errorMessage || remainingMinutes >= 0)) {
      setShowInconsistencyAlert(true)
    } else {
      checkSubmit()
    }
  }

  const checkSubmit = async () => {
    if (isAutomaticBreak) return setShowEditAutomaticBreakModal(true)

    form.handleSubmit()
  }

  // We need this refreshTimesheet method because of the EditTimesheet requests feature.
  // In case a request is created by a person with permissions to approve over the employee,
  // the request will get autoapproved and trigger the corresponding changes.
  // This means we need to refresh the timesheet but also, when submitting a new request to
  // avoid the Create/EditTimesheetRequest modal reappearing again
  const { running: refreshingTimesheet, performAction: refreshTimesheet } = useAsyncAction(
    async () => {
      await store.fetch(moment(dateFrom), moment(dateTo))
      if (isCurrentAndOpen) {
        checkShiftIsUnresolved()
      }
    }
  )

  const checkHasDisabledSubmit = () => {
    return (
      loadingBreakSettings ||
      hasDisabledSubmit(moment(date), clockOut, form.values.clockIn, form.values.clockOut)
    )
  }

  const shiftVersionHistories = useShiftVersionHistories()
  const histories = shiftVersionHistories.filter({
    shift_id: shiftId
  })
  const showVersionHistory = histories.length > 0
  const hasInputChanged = (form.touched.clockIn || form.touched.clockOut) && !form.touched.id

  const showResolveBlock =
    !readonly && hasEditTimesheetAuthorization && canOpenEditRequest && showEditModalButton

  const hasShiftInconsistencies = hasAlerts
    ? !!(inconsistencies && shiftWithInconsistency(inconsistencies, shiftId))
    : !!breakLengthInconsistency?.length

  const isUnresolved =
    (hasAlerts && hasShiftInconsistencies) || (hasMissingClockOut && shiftIsUnresolved)

  const resolveShift = isCurrentAndOpen && isUnresolved
  const showClockOutInput = !isOpen || !isCurrentAndOpen || resolveShift

  const hasInputBorderStyle = showShiftInputBorderStyles({
    isUnresolved: !isUnresolved,
    hasEditTimesheetAuthorization,
    pendingEditRequest: !!pendingEditRequest,
    isCreateShift:
      !!pendingEditRequest && !EditTimesheetRequestUtils.isCreateShift(pendingEditRequest),
    hasShiftInconsistencies: !hasShiftInconsistencies
  })

  const inputBorderStyle: React.ComponentProps<typeof Box>['border'] =
    styleShiftInputBorder(hasInputBorderStyle)

  const [open, toggle] = useToggle(false)

  return (
    <Table.Row background={background}>
      <Table.Cell colSpan={2} wide noBorder>
        <Box flexDirection="row" alignItems="center" gap="s8">
          <Box gap="s8">
            <Box flexDirection="row">
              <ShiftPopover histories={histories} />
              <Box flexDirection="row" alignItems="center" height="s48">
                <ClockIn
                  form={form}
                  readonly={isReadOnly}
                  isUnresolved={isOpen ? false : isUnresolved}
                  inputBorderStyle={isOpen ? undefined : inputBorderStyle}
                />
                <Separator />
                <ClockOut
                  form={form}
                  readonly={isReadOnly}
                  shift={shift}
                  showClockOutInput={showClockOutInput}
                  isUnresolved={isUnresolved}
                  inputBorderStyle={inputBorderStyle}
                />
              </Box>
            </Box>
            {!!hasInputChanged && (
              <Box alignItems="flexEnd">
                <Button
                  disabled={checkHasDisabledSubmit()}
                  onClick={() => checkForInconsistencies()}
                >
                  {i18n.t('common.submit')}
                </Button>
              </Box>
            )}
          </Box>
          <Box
            flexDirection="row"
            alignItems="center"
            alignSelf="center"
            gap="s8"
            height="s48"
            minWidth="s24"
          >
            {showPlusOneDay && (
              <PlusOneDay
                referenceDate={AttendanceShiftUtils.formattedDay(shift.referenceDate)}
                date={AttendanceShiftUtils.formattedDay(shift.date)}
                crossesMidnight={shift.crossesMidnight}
              />
            )}
            {showResolveBlock && (
              <>
                <Button
                  type="secondary"
                  onClick={() => setShowEditTimesheetModal(true)}
                  iconName="pencil"
                ></Button>
              </>
            )}
            {showDeleteButton && (
              <DeleteButton
                shift={shift}
                hasEditTimesheetAuthorization={hasEditTimesheetAuthorization}
                refreshTimesheet={refreshTimesheet}
                disabled={disabledDeleteBtn}
                fetchDependencies={!(TimesheetFeatures.MATERIALIZED_DATA in featureByName)}
              />
            )}
            <VersionHistory
              histories={histories}
              disabled={!showVersionHistory}
              attendanceSetting={attendanceSetting}
            />
            {observations && (
              <ObservationsMemo
                toggle={toggle}
                open={open}
                observations={observations}
                disabled={!observations}
              />
            )}
          </Box>
          {showEditAutomaticBreakModal && (
            <EditAutoBreakModal
              onConfirm={() => {
                form.handleSubmit()
                setShowEditAutomaticBreakModal(false)
              }}
              onClose={() => {
                form.resetForm()
                setShowEditAutomaticBreakModal(false)
              }}
            />
          )}
          {showEditTimesheetModal &&
            !refreshingTimesheet &&
            (pendingEditRequest ? (
              <EditRequestModal
                refreshTimesheet={refreshTimesheet}
                onCloseModal={() => setShowEditTimesheetModal(false)}
                editTimesheetRequest={pendingEditRequest}
                hasBreaksEnabled={hasBreaksEnabled}
                hasLocationEnabled={hasLocationEnabled}
                dayBreaks={dayBreaks}
                employeeId={store.employeeId}
              />
            ) : (
              <CreateRequestModal
                refreshTimesheet={refreshTimesheet}
                onCloseModal={() => setShowEditTimesheetModal(false)}
                shift={shift}
                date={moment(date)}
                dayBreaks={dayBreaks}
                hasBreaksEnabled={hasBreaksEnabled}
                hasLocationEnabled={hasLocationEnabled}
                employeeId={store.employeeId}
              />
            ))}
        </Box>
      </Table.Cell>
      <Table.Cell verticalAlign="middle" wide noBorder>
        {hasEditTimesheetAuthorization &&
          (pendingEditRequest ? (
            <PendingRequestTag request={pendingEditRequest} />
          ) : (
            <Text.Normal size="200" color="grey600">
              —
            </Text.Normal>
          ))}
      </Table.Cell>
      <ShiftDetailColumns
        shiftLocations={shiftLocations}
        shift={shift}
        projectName={projectName}
        subprojectName={subprojectName}
        selectedBreak={selectedBreak}
        checkSubmit={checkSubmit}
        form={form}
        dayBreaks={dayBreaks}
        attendanceSetting={attendanceSetting}
        hasProjects={hasProjects}
      />
      {showInconsistencyAlert && (
        <TimeInconsistenciesAlert
          errors={errors}
          shiftLengthInMinutes={shiftLengthInMinutes}
          onClose={() => setShowInconsistencyAlert(false)}
          onReview={() => setShowInconsistencyAlert(false)}
          onSave={onModalSave}
        />
      )}
    </Table.Row>
  )
}

export default observer(ShiftRow)
